/**
 * 範囲の中の除外区域 (穴) の扱い。
 *
 * 避難先の polygon は `coordinates` = 塗りエリア (外周) の配列、`holes` = 除外区域の配列で、
 * 穴がどのエリアに属するかは持たない (公式の区域は複数エリア + 穴の組み合わせがあり、
 * 外周の並びを変えずに穴を足すため)。属するエリアは位置で決める。
 */
import {
  booleanPointInPolygon,
  lineIntersect,
  lineString,
  point as turfPoint,
  polygon as turfPolygon,
} from '@turf/turf';
import { pointInsideRing } from './polygon-utils';
import type { LngLat } from '@/types/property';

type Ring = Array<[number, number]>;

const closed = (r: Ring): Ring => {
  const a = r[0]!;
  const b = r[r.length - 1]!;
  return a[0] === b[0] && a[1] === b[1] ? r : [...r, a];
};

/** 交点が「入る」交差かを確かめるため、交点から線分の先へ進めて穴の内外を見る距離 (度)。約0.5m。 */
const ENTRY_PROBE_DEG = 0.000005;

/**
 * 経路が除外区域 (穴) に入る所で切る。入らなければそのまま返す。
 *
 * 経路の後処理の最後に掛ける。範囲に入ったあとの延長や、範囲の中を進む候補経路が、
 * 範囲の中の除外区域へ入ることがある (実測: 江戸川南部一帯 — 新左近川の水面が除外区域で、
 * 矢印の先が 150m 水面を横切っていた)。頂点ではなく線分と穴の交差で判定する
 * (細い除外区域を頂点が跨いでも拾う)。縁を掠めるだけの交差は切らない。
 * 始点を内包する穴は対象外 (物件が除外区域の中にある場合、出発直後で切れてしまう)。
 */
export function trimRouteAtHoles(route: Ring, holes: Ring[] | undefined): Ring {
  if (!holes || holes.length === 0 || route.length < 2) return route;
  const start = turfPoint(route[0]!);
  const polys = holes
    .filter((h) => h.length >= 4)
    .map((h) => turfPolygon([closed(h)]))
    .filter((poly) => !booleanPointInPolygon(start, poly));
  if (polys.length === 0) return route;

  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1]!;
    const b = route[i]!;
    const seg = lineString([a, b]);
    const hits: Array<[number, number]> = polys.flatMap(
      (poly) =>
        lineIntersect(seg, poly).features.map(
          (f) => f.geometry.coordinates as [number, number],
        ),
    );
    hits.sort(
      (p, q) =>
        (p[0] - a[0]) ** 2 + (p[1] - a[1]) ** 2 -
        ((q[0] - a[0]) ** 2 + (q[1] - a[1]) ** 2),
    );
    for (const c of hits) {
      const dx = b[0] - c[0];
      const dy = b[1] - c[1];
      const len = Math.hypot(dx, dy);
      if (len === 0) continue;
      const k = Math.min(1, ENTRY_PROBE_DEG / len);
      const probe = turfPoint([c[0] + dx * k, c[1] + dy * k]);
      if (polys.some((poly) => booleanPointInPolygon(probe, poly))) {
        return [...route.slice(0, i), c];
      }
    }
  }
  return route;
}

/** 外周 ring の内側にある穴だけを返す (そのエリアを塗るときに抜く穴)。 */
export function holesWithin(ring: Ring, holes: Ring[] | undefined): Ring[] {
  if (!holes || holes.length === 0) return [];
  return holes.filter((h) =>
    h.some(([lng, lat]) => pointInsideRing({ lng, lat }, ring)),
  );
}

/** 点がどれかの穴 (除外区域) の中にあるか。 */
export function insideAnyHole(p: LngLat, holes: Ring[] | undefined): boolean {
  return !!holes && holes.some((h) => pointInsideRing(p, h));
}
